import type { ReactNode } from 'react';
import { useAuthStore } from '@/stores/authStore';

interface PermissionGuardProps {
  /** Require this single permission */
  permission?: string;
  /** Require ANY of these permissions */
  anyOf?: string[];
  /** Require ALL of these permissions */
  allOf?: string[];
  children: ReactNode;
  /** Rendered when permission is denied (default: null) */
  fallback?: ReactNode;
}

/**
 * Conditionally renders children based on the current user's permissions.
 *
 * Usage:
 * ```tsx
 * <PermissionGuard permission="work_orders.delete">
 *   <DeleteButton />
 * </PermissionGuard>
 * ```
 */
export function PermissionGuard({
  permission,
  anyOf,
  allOf,
  children,
  fallback = null,
}: PermissionGuardProps) {
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const hasAnyPermission = useAuthStore((s) => s.hasAnyPermission);
  const hasAllPermissions = useAuthStore((s) => s.hasAllPermissions);

  let allowed = false;
  if (permission) allowed = hasPermission(permission);
  else if (anyOf) allowed = hasAnyPermission(anyOf);
  else if (allOf) allowed = hasAllPermissions(allOf);
  else allowed = true;

  return allowed ? <>{children}</> : <>{fallback}</>;
}
